import React, { createContext, useContext, useState, useEffect } from "react";
import Cookies from "js-cookie";
import { axiosInstance, setLoggingOut } from "./axiosApiInstance";

interface AuthContextType {
  authenticated: boolean;
  setAuthenticated: (value: boolean) => void;
  setEmail: (value: string) => void;
  loading: boolean;
  logout: () => void;
  email: string;
  accountStatus: string;
  setAccountStatus: (value: string) => void;
  /** Legacy single role string ("mycor_dev_team" | "user"), derived from the
   *  FGA platform-admin check for back-compat. Prefer `roles`/`isAdmin`. */
  role: string;
  /** FGA platform roles ("admin", "can_publish_dataset",
   *  "can_manage_public_catalog"). Source of truth. */
  roles: string[];
  isAdmin: boolean;
  /** May publish datasets to everyone (higher role). */
  canPublishDataset: boolean;
  /** May create catalog models + build/curate public datasets. */
  canManagePublicCatalog: boolean;
  tosAccepted: boolean;
  ppAccepted: boolean;
  setTosAccepted: (value: boolean) => void;
  setPpAccepted: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProviderInner = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [accountStatus, setAccountStatus] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [ppAccepted, setPpAccepted] = useState(false);

  const logout = async () => {
    setLoggingOut(true);
    try {
      await axiosInstance.get("/action/auth/logout");
    } catch (e) {
      console.log("Error in logout: ", e);
    }
    Cookies.remove("token");
    Cookies.remove("session_id");
    const preserved = ["theme", "sidebarOpen"];
    const saved = preserved.map((k) => [k, localStorage.getItem(k)] as const);
    localStorage.clear();
    for (const [k, v] of saved) if (v != null) localStorage.setItem(k, v);
    sessionStorage.clear();
    setAuthenticated(false);
    setEmail("");
    setAccountStatus("");
    setRole("");
    setRoles([]);
    window.location.href = "/";
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axiosInstance.get("/api/auth/check");

        if (res.data.authenticated) {
          setAuthenticated(true);
          setEmail(res.data.email || "");

          if (res.data.account_status) {
            setAccountStatus(res.data.account_status.toLowerCase());
          }
          if (res.data.role) {
            setRole(res.data.role);
          }
          setRoles(Array.isArray(res.data.roles) ? res.data.roles : []);
          setTosAccepted(!!res.data.tos_accepted);
          setPpAccepted(!!res.data.pp_accepted);

          // OAuth catalog flow: when the user signs in via Google
          // OAuth they bypass /login on the way back (backend
          // redirects straight to /models), so check here for the
          // stashed return_to and redirect once auth is confirmed.
          // LoginPage handles the email+password path itself.
          const returnTo = sessionStorage.getItem("oauth_return_to");
          if (returnTo) {
            sessionStorage.removeItem("oauth_return_to");
            window.location.href = returnTo;
            return;
          }
        } else {
          setAuthenticated(false);
          setAccountStatus("");
          setRole("");
          setRoles([]);
          setTosAccepted(false);
          setPpAccepted(false);
        }
      } catch (error) {
        setAuthenticated(false);
        setAccountStatus("");
        setRole("");
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        setEmail,
        setAuthenticated,
        authenticated,
        loading,
        logout,
        email,
        accountStatus,
        setAccountStatus,
        role,
        roles,
        isAdmin: roles.includes("admin"),
        canPublishDataset: roles.includes("can_publish_dataset"),
        canManagePublicCatalog: roles.includes("can_manage_public_catalog"),
        tosAccepted,
        ppAccepted,
        setTosAccepted,
        setPpAccepted,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }) => {
  return <AuthProviderInner>{children}</AuthProviderInner>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
